import { isRecord } from '@/utils/objects';
import type { EventNode } from '../types';
import { extractFilters, toNumber, toString } from './parse-helpers';

/**
 * Parse event conditions.
 * Handles: { event: { type: 'event', name, filters }, times, within, inverse }
 *          { type: 1, event_filter: { times, within, inverse }, event_type_id, event: { name } }
 *          { type: 'event', name, times, within, inverse }
 */
export function parseEvent(raw: Record<string, unknown>): EventNode | null {
  // Backend format first — takes priority when event_filter is present
  // because the Ember format branch would incorrectly use raw.inverse instead of event_filter.inverse
  if ((raw.type === 1 || raw.type === '1') && isRecord(raw.event_filter)) {
    const ef = raw.event_filter;
    // Event name may be in raw.event.name or raw.event_name
    const eventObject = isRecord(raw.event) ? raw.event : undefined;
    const eventType = toString(eventObject?.type ?? eventObject?.ui_type);

    // Skip non-event types — they're handled by other parsers
    if (eventType != null && eventType !== 'event') return null;

    const name = toString(eventObject?.name ?? raw.event_name) ?? 'event';
    const parsed = extractFilters(eventObject?.filters ?? ef.filters);
    return {
      type: 'event',
      name,
      inverse: ef.inverse === true,
      times: toNumber(ef.times),
      within: toNumber(ef.within),
      filters: parsed?.filters,
      filtersOperator: parsed?.operator,
    };
  }

  // Ember format: { event: { type: 'event', name }, times, within, inverse }
  const event = isRecord(raw.event) ? raw.event : undefined;
  if (event?.type === 'event') {
    const name = toString(event.name) ?? 'event';
    const parsed = extractFilters(event.filters);
    return {
      type: 'event',
      name,
      inverse: raw.inverse === true,
      times: toNumber(raw.times),
      within: toNumber(raw.within),
      filters: parsed?.filters,
      filtersOperator: parsed?.operator,
    };
  }

  // Direct format: { type: 'event', name, ... }
  if (raw.type === 'event') {
    const name = toString(raw.name) ?? 'event';
    const parsed = extractFilters(raw.filters);
    return {
      type: 'event',
      name,
      inverse: raw.inverse === true,
      times: toNumber(raw.times),
      within: toNumber(raw.within),
      filters: parsed?.filters,
      filtersOperator: parsed?.operator,
    };
  }

  return null;
}
